import { useRef, useEffect, useState, memo, useMemo } from "react";
import { cn } from "@/lib/utils";
import {
  IconCheck,
  IconLoader2,
  IconAlertCircle,
  IconCircleDot,
  IconChevronDown,
  IconChevronRight,
} from "@tabler/icons-react";
import type { AnalysisLog, SubLogItem } from "@/api/useAnalysisLogs";

// Track which logs/sublogs have been seen to avoid re-animating them
const seenLogIds = new Set<string>();
const seenSubLogIds = new Set<string>();

interface AnalysisLogDisplayProps {
  logs: AnalysisLog[];
  maxHeight?: string;
  className?: string;
}

// Stages that support collapsible sub-logs
const COLLAPSIBLE_STAGES = [
  "analyze_pages",
  "group_content",
  "extract_templates",
];

// Ordered list of analysis stages for step numbering
const STAGE_ORDER = [
  "initialize",
  "detect_framework",
  "scan_files",
  "analyze_pages",
  "group_content",
  "extract_templates",
  "save_results",
  "complete",
];

export function AnalysisLogDisplay({
  logs,
  maxHeight,
  className,
}: AnalysisLogDisplayProps) {
  const scrollRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when new logs arrive
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [logs]);

  if (logs.length === 0) {
    return null;
  }

  // Calculate step numbers for each log based on stage order
  const totalSteps = STAGE_ORDER.length;
  const getStepNumber = (stage: string) => {
    const index = STAGE_ORDER.indexOf(stage);
    return index >= 0 ? index + 1 : null;
  };

  // Compute which log should show step number for each stage (first log per stage)
  const stageFirstLogId = useMemo(() => {
    const map = new Map<string, string>();
    for (const log of logs) {
      if (!map.has(log.stage) && getStepNumber(log.stage) !== null) {
        map.set(log.stage, log.id);
      }
    }
    return map;
  }, [logs]);

  return (
    <div className={cn("relative flex flex-col", className)}>
      <div
        ref={scrollRef}
        className="overflow-y-auto flex-1 min-h-0 font-mono overflow-x-hidden flex flex-col justify-end"
        style={maxHeight ? { maxHeight } : undefined}
      >
        <div className="space-y-1 mt-auto">
          {logs.map((log) => (
            <LogEntry
              key={log.id}
              log={log}
              stepNumber={getStepNumber(log.stage)}
              totalSteps={totalSteps}
              showStepNumber={stageFirstLogId.get(log.stage) === log.id}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

// Memoized status icon getter to avoid re-renders
const getStatusIcon = (status: string) => {
  switch (status) {
    case "completed":
      return <IconCheck className="w-3.5 h-3.5 text-green-500" />;
    case "started":
    case "progress":
    case "in_progress":
      return <IconLoader2 className="w-3.5 h-3.5 text-primary animate-spin" />;
    case "error":
      return <IconAlertCircle className="w-3.5 h-3.5 text-destructive" />;
    case "pending":
      return <IconCircleDot className="w-3.5 h-3.5 text-muted-foreground/40" />;
    default:
      return <IconCircleDot className="w-3.5 h-3.5 text-muted-foreground" />;
  }
};

const LogEntry = memo(function LogEntry({
  log,
  stepNumber,
  totalSteps,
  showStepNumber,
}: {
  log: AnalysisLog;
  stepNumber: number | null;
  totalSteps: number;
  showStepNumber: boolean;
}) {
  const hasSubLogs = log.subLogs && log.subLogs.length > 0;
  const isCollapsible = COLLAPSIBLE_STAGES.includes(log.stage);
  const [isExpanded, setIsExpanded] = useState(true);

  // Check if this is a new log that should animate
  const isNew = !seenLogIds.has(log.id);
  if (isNew) {
    seenLogIds.add(log.id);
  }

  return (
    <div className={cn(isNew && "animate-in fade-in duration-200")}>
      <div
        className={cn(
          "flex items-start gap-2 text-xs py-1",
          log.status === "error" && "text-destructive",
          isCollapsible &&
            hasSubLogs &&
            "cursor-pointer hover:bg-muted/30 rounded -mx-1 px-1"
        )}
        onClick={() => {
          if (isCollapsible && hasSubLogs) {
            setIsExpanded(!isExpanded);
          }
        }}
      >
        {/* Step number - show for logs that originally appeared as "started" */}
        {showStepNumber && stepNumber !== null && (
          <div className="flex-shrink-0 w-8 text-muted-foreground/60 text-[10px] mt-0.5 text-right tabular-nums">
            {stepNumber}/{totalSteps}
          </div>
        )}
        {/* Spacer when step number is hidden */}
        {!showStepNumber && <div className="flex-shrink-0 w-8" />}

        {/* Expand/collapse chevron for collapsible stages */}
        {isCollapsible && hasSubLogs ? (
          <div className="flex-shrink-0 mt-0.5 text-muted-foreground">
            {isExpanded ? (
              <IconChevronDown className="w-3.5 h-3.5" />
            ) : (
              <IconChevronRight className="w-3.5 h-3.5" />
            )}
          </div>
        ) : (
          <div className="flex-shrink-0 mt-0.5 w-3.5" />
        )}

        {/* Status icon */}
        <div className="flex-shrink-0 mt-0.5">{getStatusIcon(log.status)}</div>

        {/* Message */}
        <div className="flex-1 min-w-0">
          <span
            className={cn(
              log.status === "completed" && "text-muted-foreground",
              log.status === "started" && "text-foreground font-medium",
              log.status === "progress" && "text-foreground"
            )}
          >
            {log.message}
          </span>
          {log.details && !hasSubLogs && (
            <span className="text-muted-foreground/70 text-[10px] block mt-0.5">
              {log.details}
            </span>
          )}
        </div>
      </div>

      {/* Sub-logs (collapsible) */}
      {hasSubLogs && isExpanded && (
        <div className="ml-16 pl-3 border-l border-border/50 space-y-0.5 mt-0.5 mb-1">
          {log.subLogs!.map((subLog) => (
            <SubLogEntry key={subLog.id} subLog={subLog} />
          ))}
        </div>
      )}
    </div>
  );
});

const SubLogEntry = memo(function SubLogEntry({
  subLog,
}: {
  subLog: SubLogItem;
}) {
  // Check if this is a new sublog that should animate
  const isNew = !seenSubLogIds.has(subLog.id);
  if (isNew) {
    seenSubLogIds.add(subLog.id);
  }

  return (
    <div
      className={cn(
        "flex items-start gap-2 text-[11px] py-0.5",
        subLog.status === "error" && "text-destructive",
        isNew && "animate-in fade-in duration-200"
      )}
    >
      <div className="flex-shrink-0 mt-0.5">{getStatusIcon(subLog.status)}</div>
      <span
        className={cn(
          subLog.status === "completed" && "text-muted-foreground",
          subLog.status === "in_progress" && "text-foreground",
          subLog.status === "pending" && "text-muted-foreground/60"
        )}
      >
        {subLog.message}
      </span>
    </div>
  );
});
